/**
 * Minimal bech32 (BIP-173) decode for `npub` → hex pubkey.
 *
 * Only what the payment path needs: parse the operator's configured npub to
 * the 32-byte hex pubkey compared against zap receipt `p` tags. No bech32m,
 * no encoding — decoding NIP-19 npubs is bech32-only.
 *
 * Plain ES module: shared by the Worker (src/pay.ts) and the browser UI
 * (the pay-to-relay receipt submission flow).
 *
 * @module shared/bech32
 */

const CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';
const CHARKEY = new Map([...CHARSET].map((c, i) => [c, i]));

function polymod(values) {
  const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;
  for (const v of values) {
    const top = chk >> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) {
      if ((top >> i) & 1) chk ^= GEN[i];
    }
  }
  return chk;
}

function hrpExpand(hrp) {
  return [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31));
}

/** Convert a buffer between bit widths (BIP-173 convertbits). */
function convertBits(data, fromBits, toBits, pad) {
  let acc = 0;
  let bits = 0;
  const ret = [];
  const maxv = (1 << toBits) - 1;
  for (const value of data) {
    if (value < 0 || value >> fromBits !== 0) return null;
    acc = (acc << fromBits) | value;
    bits += fromBits;
    while (bits >= toBits) {
      bits -= toBits;
      ret.push((acc >> bits) & maxv);
    }
  }
  if (pad) {
    if (bits > 0) ret.push((acc << (toBits - bits)) & maxv);
  } else if (bits >= fromBits || ((acc << (toBits - bits)) & maxv) !== 0) {
    return null;
  }
  return ret;
}

/**
 * Encode a hex pubkey as a NIP-19 npub. Returns null for malformed input.
 *
 * @param {string} hex 64-char hex pubkey
 * @returns {string | null}
 */
export function hexToNpub(hex) {
  try {
    if (!/^[0-9a-fA-F]{64}$/.test(hex)) return null;
    const bytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    const words = convertBits([...bytes], 8, 5, true);
    if (!words) return null;
    const combined = hrpExpand('npub').concat(words).concat([0, 0, 0, 0, 0, 0]);
    const mod = polymod(combined) ^ 1;
    const checksum = [];
    for (let i = 0; i < 6; i++) checksum.push((mod >> (5 * (5 - i))) & 31);
    return 'npub1' + [...words, ...checksum].map((d) => CHARSET[d]).join('');
  } catch {
    return null;
  }
}

/**
 * Decode a NIP-19 npub to a lowercase hex pubkey. Returns null for anything
 * malformed or not an npub.
 *
 * @param {string} npub
 * @returns {string | null}
 */
export function npubToHex(npub) {
  try {
    const value = String(npub).trim().toLowerCase();
    const split = value.lastIndexOf('1');
    if (split < 1 || split + 7 > value.length) return null;
    const hrp = value.slice(0, split);
    if (hrp !== 'npub') return null;

    const dataPart = value.slice(split + 1);
    const data = [];
    for (const c of dataPart) {
      const d = CHARKEY.get(c);
      if (d === undefined) return null;
      data.push(d);
    }
    if (data.length < 6) return null;

    const checksum = polymod(hrpExpand(hrp).concat(data));
    if (checksum !== 1) return null;

    const words = convertBits(data.slice(0, -6), 5, 8, false);
    if (!words || words.length !== 32) return null;

    return words.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}
